import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { api } from "../../../singleton/axios";
import { NotificationChannel, NotificationPreferences } from "../types";
import { NOTIFICATION_PREFERENCES_QUERY_KEY } from "./useNotificationPreferences";

/**
 * Which wording of the preferences card the person was shown. Stored with
 * every change as consent provenance — bump it when the card's copy changes.
 */
export const SETTINGS_WORDING_VERSION = "settings-v1";

interface UpdatePreferenceVariables {
  category: string;
  channel: NotificationChannel;
  enabled: boolean;
}

/** Flip one switch. Optimistic, rolled back if the server refuses. */
export const useUpdateNotificationPreference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdatePreferenceVariables) => {
      const { data } = await api.put<NotificationPreferences>("/notification-preferences", {
        ...variables,
        wordingVersion: SETTINGS_WORDING_VERSION,
      });
      return data;
    },

    onMutate: async ({ category, channel, enabled }) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATION_PREFERENCES_QUERY_KEY });
      const previous = queryClient.getQueryData<NotificationPreferences>(
        NOTIFICATION_PREFERENCES_QUERY_KEY,
      );
      queryClient.setQueryData<NotificationPreferences>(
        NOTIFICATION_PREFERENCES_QUERY_KEY,
        (old) => {
          if (!old) return old;
          return {
            preferences: old.preferences.map((preference) =>
              preference.category === category
                ? { ...preference, channels: { ...preference.channels, [channel]: enabled } }
                : preference,
            ),
          };
        },
      );
      return { previous };
    },

    // The response is the whole, authoritative list.
    onSuccess: (data) => {
      queryClient.setQueryData(NOTIFICATION_PREFERENCES_QUERY_KEY, data);
    },

    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(NOTIFICATION_PREFERENCES_QUERY_KEY, context.previous);
      }
      enqueueSnackbar("Couldn't save that preference. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    },
  });
};
